import { Button, Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {Link, useHistory} from 'react-router-dom';

import ProfileCard from '../../components/Cards/ProfileCard';
import Icon from '../../components/Icon/Icon';
import Spinner from '../../components/Spinner/Spinner';
import Tag from '../../components/Tag/Tag';
import UserAverageRating from '../../components/UserAverageRating/UserAverageRating';

import { useBackofficeRating } from './useBackofficeRating';

import { Status } from './BackofficeRating.model';
import { Score } from '../../Helpers/useScoreHelper';

import styles from './BackofficeRating.module.scss';

const BackofficeRating = () => {
	const {
		approveReview,
		assignRating,
		formatDate,
		isAssigned,
		isAssignedAdminDeleted,
		reassignRating,
		rejectReview,
		state
	} = useBackofficeRating();
	const {
		admins,
		loading,
		rating,
		user
	} = state;

	const intl = useIntl();
	const history = useHistory()


	const getTagColorByRole = (role: string) => {
		switch (role) {
			case 'Mentee': return 'grey';
			case 'Mentor': return 'blue';
			case 'Admin': return 'red';
		}
	}

	const getTagColorByStatus = (status: Status) => {
		switch (status) {
			case 'Accepted': return "green";
			case 'Rejected': return "red";
		}
	}

	const profileCardActions = [<div className={styles.cardFooter}>
		<FormattedMessage id="ratings.reviewedOn" />
		{rating && <span>{formatDate(rating.ratedAt)}</span>}
	</div>]


	const renderProfileCard = () => {
		return user && <ProfileCard
			actions={profileCardActions}
			fitContainer
			showData
			data={user}
		>
			<div className={styles.rating}>
				{user.averageScore && <UserAverageRating score={user.averageScore} />}
			</div>
		</ProfileCard>
	}

	const renderActions = () => {
		return isAssigned() && <div className={styles.statusActions}>
			<Button type="link" size="large" className={styles.redBtn} onClick={rejectReview}>
				<Icon name="trash" color="accent" width="12px" />
				<FormattedMessage id="buttons.reject" />
			</Button>
			<Button type="primary" size="large" className={styles.greenBtn} onClick={approveReview}>
				<Icon name="check" color="white" width="10px" />
				<FormattedMessage id="buttons.approve" />
			</Button>
		</div>
	}

	return <div className={styles.pageContainer}>
		<div className={`${styles.pageSider} ${styles.topBorder} ${styles.noPadding}`}>
			{renderProfileCard()}
		</div>

		{rating && <div className={styles.pageContent}>
			<div className={`${styles.mobileTopper} ${styles.topBorder} ${styles.noPadding}`}>
				{renderProfileCard()}
			</div>
			<div className={styles.mainActions}>
				<div className={`${styles.breadcrumbs}`}>
					<Link to={{ pathname: "/users", state: { activeTab: 4 } }}><FormattedMessage id="navigation.ratings" /> / </Link>
					<b className={styles.displayBlock}>{user?.name}<FormattedMessage id="basics.possesiveNoun"/> <FormattedMessage id="ratings.review" /></b>
					<Select
						className={styles.select}
						defaultValue={
						rating.assignedTo
							? !isAssignedAdminDeleted(rating.assignedTo)
								? rating.assignedTo
								: intl.formatMessage({ id: 'userLabels.deletedUser' })
							: undefined
					}
						onChange={(value) => {
							if (rating.assignedTo) reassignRating(value, rating);
							else assignRating(value, rating);
						}}
						placeholder={<div className={styles.placeholder}><FormattedMessage id="ratings.assignNow" /></div>}>
						{admins?.map((admin, index) => {
							return <Select.Option value={admin.userId} key={admin.userId}>
								<div className={styles.option}>
									<div className={styles.avatar}>
										{admin.imageId && !admin.isDeleted ?
											<img src={admin.imageId} alt="" /> :
											<Icon
												color="light"
												height="12px"
												name="user"
												width="12px"
											/>}
									</div>
									<span>{!admin.isDeleted ? `${admin.firstName} ${admin.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}</span>
								</div>
							</Select.Option>
						})}
					</Select>
				</div>
				<div className={styles.actions}>
					{rating.status !== "Accepted" && rating.status !== "Rejected" ?
						renderActions() :
						<div className={styles.statusActions}><Tag noBackground color={getTagColorByStatus(rating.status)}>{intl.formatMessage({ id: `status.rating.${rating.status}` })}</Tag> </div>}
				</div>
			</div>

			<div className={styles.content}>
				<Spinner spinning={loading} />
				<div className={styles.ratingWrapper}>
					<div className={styles.row}>
						<span className={styles.label}><FormattedMessage id="ratings.labels.rating" /></span>
						<div className={styles.badge}>
							{rating.score === Score.Substandard && <Icon name="bad-star" type="custom" color="gray" width="25px" height="25px" />}
							{rating.score === Score.Good && <Icon name="star" type="solid" color="accent" width="25px" height="25px" />}
							{rating.score === Score.Exceptional && <Icon name="stars" color="blue" type="solid" width="25px" height="25px" />}
							<span><FormattedMessage id={`ratings.score.${rating.score}`} /></span>
						</div>
					</div>
					<div className={styles.row}>
						<span className={styles.label}><FormattedMessage id="ratings.labels.message" /></span>
						<p>
							{rating.feedback}
						</p>
					</div>
					<div className={styles.row}>
						<span className={styles.label}><FormattedMessage id="ratings.labels.reviewedBy" /></span>
						<div className={styles.user}>
							<div className={styles.avatar} onClick={!rating.user.isDeleted ? () => history.push(`/users/${rating.user.userId}`): undefined}>
								{rating.user.imageId && !rating.user.isDeleted?
									<img src={rating.user.imageId} alt="" /> :
									<Icon
										color="light"
										height="12px"
										name="user"
										width="12px"
									/>}
							</div>
							<div>
								<h4>{!rating.user.isDeleted  ? `${rating.user.firstName} ${rating.user.lastName}` : intl.formatMessage({id: "userLabels.deletedUser"})}</h4>
								<Tag color={getTagColorByRole(rating.user.role)}>{rating.user.role}</Tag>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>}
	</div>
}
export default BackofficeRating;